import { useState, useRef, useEffect, useCallback } from 'react';
import clamp from 'lodash/clamp';
import nextStep from '@andersaloof/next-step';

const DEFAULT_DURATION = 2000;

export default function useSlides(initialItems = null, initialIndex = 0) {
  const [items] = useState(initialItems);
  const [index, setIndex] = useState(initialIndex);
  const [current, setCurrent] = useState(null);
  const [duration, setDuration] = useState(false);
  const [progress, setProgress] = useState(0);
  const [start, setStart] = useState(false);
  const [active, setActive] = useState(initialItems.length > 1);

  const rafId = useRef(null);

  // Set duration when current slide changes, if it's not a video
  useEffect(() => {
    if (index !== null) {
      const nextSlide = items[index];

      // Only set duration if next slide is not a video slide
      const nextDuration = DEFAULT_DURATION;

      setProgress(0); // Reset progress to 0 when index changes
      setStart(false); // Reset start timestamp
      setCurrent(nextSlide);
      setDuration(nextDuration);
    }
  }, [index]);

  // Preload next image when image changes
  useEffect(() => {
    if (index !== null) {
      const nextIndex = nextStep(index, items.length - 1, 1);
      const nextImage = items[nextIndex].image;
      const srcset = nextImage.images.map(
        (image) => `${image.url} ${parseInt(image.width, 10)}w`
      );
      const sizes = nextImage.sizes ? nextImage.sizes.join(', ') : '100vw';
      const image = new Image();
      // image.onload = () => {
      //   console.warn('Next image is preloaded!');
      // };
      image.sizes = sizes;
      image.srcset = srcset;
    }
  }, [index]);

  // Update progress via RAF when active
  useEffect(() => {
    if (active) {
      if (!duration) {
        if (rafId.current) {
          cancelAnimationFrame(rafId.current);
          rafId.current = null;
        }
      } else if (progress < 1) {
        let myStart = start || Date.now();

        if (!start) {
          setStart(myStart);
        }

        rafId.current = requestAnimationFrame(() => {
          const elapsed = Date.now() - myStart;
          const nextProgress = clamp(elapsed / duration, 0, 1);
          setProgress(nextProgress);
        });
      }

      return () => {
        cancelAnimationFrame(rafId.current);
      };
    }
  }, [active, index, duration, progress]);

  // Proceed to next slide automatically when progress reaches 1
  useEffect(() => {
    if (progress >= 1) {
      setIndex(nextStep(index, items.length - 1, 1));
    }
  }, [progress]);

  // Updates the active state, and resets the start timestamp if we're in the middle of a progress
  const updateActive = (nextActive) => {
    if (items.length > 1) {
      if (duration && progress > 0) {
        const myStart = Date.now() - duration * progress;
        setStart(myStart);
      }
      setActive(nextActive);
    }
  };

  const state = {
    index,
    current,
    duration,
    progress,
    active,
  };

  const api = {
    setActive: updateActive,
  };

  return [state, api];
}
